/* eslint-disable react/prop-types -- internal LMS UI; props shaped by parent layout */
import { Modal } from "react-bootstrap";
import { FaCheck, FaTimes } from "react-icons/fa";

import {
  ADDITIONAL_LOAN_BANNER,
  ADDITIONAL_LOAN_IMAGES,
  ADDITIONAL_LOAN_MODAL_ACTION,
  ADDITIONAL_LOAN_MODAL_CONFIG,
} from "./additional_loan_overview_banner.config";
import { useAdditionalLoanOverviewBanner } from "./useAdditionalLoanOverviewBanner";

function StatusBadge({ tone }) {
  const isSuccess = tone === "success";
  return (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
      style={{
        width: 48,
        height: 48,
        background: isSuccess ? "#22c55e" : "#ef4444",
      }}
      aria-hidden
    >
      {isSuccess ? <FaCheck color="#fff" size={22} /> : <FaTimes color="#fff" size={22} />}
    </div>
  );
}

function LoanApplyModal({ show, variant, onHide, onModalAction, creatingLoan }) {
  const definition = variant ? ADDITIONAL_LOAN_MODAL_CONFIG[variant] : null;

  if (!show || !definition) {
    return null;
  }

  const illustrationSrc = ADDITIONAL_LOAN_IMAGES[definition.illustrationKey];

  const closeBtn = (
    <button
      type="button"
      className="btn btn-light rounded-circle p-2 position-absolute top-0 end-0 m-3 border-0"
      style={{ width: 40, height: 40 }}
      onClick={onHide}
      aria-label="Close"
    >
      <FaTimes className="text-dark m-auto" size={16} />
    </button>
  );

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName="border-0" contentClassName="border-0 shadow rounded-4">
      <Modal.Body className="text-center px-4 pt-5 pb-4 position-relative">
        {closeBtn}
        <div className="mb-2">
          <StatusBadge tone={definition.statusBadge} />
        </div>
        <img src={illustrationSrc} alt="" className="mx-auto d-block mb-3" style={{ maxHeight: 140, width: "auto" }} />
        <h2 className="fs-5 fw-bold text-black mb-2">{definition.title}</h2>
        <p className="text-secondary small mb-4 px-1">{definition.description}</p>
        <div className="d-flex flex-column gap-0">
          {definition.actions.map((action, index) => {
            const isProceed = action.id === ADDITIONAL_LOAN_MODAL_ACTION.PROCEED;
            const label =
              isProceed && creatingLoan && action.labelLoading ? action.labelLoading : action.label;
            const isMuted = action.tone === "muted";
            const isLast = index === definition.actions.length - 1;

            return (
              <button
                key={action.id}
                type="button"
                className={`btn rounded-pill w-100 py-2 fw-semibold ${isMuted ? "text-dark" : "btn-dark"} ${!isLast ? "mb-2" : ""}`}
                style={isMuted ? { background: "#f0f0f0" } : undefined}
                disabled={isProceed && creatingLoan}
                onClick={() => onModalAction(action.id)}
              >
                {label}
              </button>
            );
          })}
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default function AdditionalLoanOverviewBanner({ loanUUID, loanSummary }) {
  const {
    modalVariant,
    closeModal,
    onModalAction,
    handleBannerCta,
    creatingLoan,
    hasChildLoan,
  } = useAdditionalLoanOverviewBanner(loanUUID, loanSummary);

  const bannerCopy = hasChildLoan ? ADDITIONAL_LOAN_BANNER.copy.inProgress : ADDITIONAL_LOAN_BANNER.copy.newApplication;

  return (
    <>
      <div
        className="rounded-4 p-3 p-md-3 d-flex flex-column flex-md-row align-items-center gap-3 gap-md-4 mb-3"
        style={{ background: ADDITIONAL_LOAN_BANNER.gradient }}
      >
        <img
          src={ADDITIONAL_LOAN_IMAGES.runningPerson}
          alt=""
          className="flex-shrink-0"
          style={{ height: 96, width: "auto", maxWidth: "100%", objectFit: "contain" }}
        />
        <div className="text-center text-md-start">
          <div className="fw-bold fs-5 text-black">{ADDITIONAL_LOAN_BANNER.headline}</div>
          <div className="text-black" style={{ opacity: 0.85, fontSize: "0.95rem" }}>
            {bannerCopy.subheadline}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-dark rounded-pill px-4 py-2 fw-semibold flex-shrink-0 align-self-stretch align-self-md-center"
          onClick={() => void handleBannerCta()}
        >
          {bannerCopy.cta}
        </button>
      </div>

      <LoanApplyModal
        show={Boolean(modalVariant)}
        variant={modalVariant}
        onHide={closeModal}
        onModalAction={onModalAction}
        creatingLoan={creatingLoan}
      />
    </>
  );
}
